import { Component } from '@theme/component';
import { Scroller } from '@theme/scrolling';

/** Subpixel and browser-zoom tolerance for scroll-boundary comparisons. */
const EDGE_TOLERANCE = 1;

/**
 * Drives the prev/next arrows on the category rail. Scrolling itself stays pure CSS.
 *
 * Deliberately not <slideshow-component>: that marks off-screen slides inert and
 * aria-hidden, which would drop off-screen category links out of the a11y tree.
 *
 * LTR only — the theme sets no `dir`. Under RTL, scrollLeft goes negative or reversed,
 * so the boundary maths and the caret rotations would both need revisiting.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} track
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
class CollectionSlider extends Component {
  requiredRefs = ['track'];

  /** @type {Scroller | undefined} */
  #scroller;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    // onScroll only fires for user-initiated scrolls, so onScrollEnd is what keeps the
    // arrows in sync after our own programmatic paging.
    this.#scroller = new Scroller(this.refs.track, {
      onScroll: this.#update,
      onScrollEnd: this.#update,
    });

    // First callback lands after layout, before paint — that's the initial measurement.
    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(this.refs.track);

    // A webfont swap resizes the cards without resizing the track, so RO won't fire.
    document.fonts?.ready.then(this.#update);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#scroller?.destroy();
    this.#resizeObserver?.disconnect();
  }

  next() {
    this.#page(1);
  }

  previous() {
    this.#page(-1);
  }

  /**
   * Pages to a real snap point. Cards are content-sized, so a fixed pixel delta would
   * land off-snap and get silently corrected by `scroll-snap-type: x mandatory`.
   *
   * @param {1 | -1} direction
   */
  #page(direction) {
    const cards = /** @type {HTMLElement[]} */ ([...this.refs.track.children]);
    if (cards.length === 0) return;

    const viewport = this.refs.track.getBoundingClientRect();
    const target =
      direction === 1 ? this.#nextTarget(cards, viewport) : this.#previousTarget(cards, viewport);

    // No target means we're already at the boundary; the aria-disabled arrow is a no-op.
    if (!target) return;

    this.#scroller?.by(target.getBoundingClientRect().left - viewport.left);
  }

  /**
   * First card cut off past the trailing edge.
   *
   * @param {HTMLElement[]} cards
   * @param {DOMRect} viewport
   */
  #nextTarget(cards, viewport) {
    return cards.find((card) => card.getBoundingClientRect().right > viewport.right + EDGE_TOLERANCE);
  }

  /**
   * Furthest-back off-screen card still within one viewport, so a click pages back by at
   * most one screen. Falls back to the nearest when a single card exceeds the viewport.
   *
   * @param {HTMLElement[]} cards
   * @param {DOMRect} viewport
   */
  #previousTarget(cards, viewport) {
    const offscreen = cards.filter(
      (card) => card.getBoundingClientRect().left < viewport.left - EDGE_TOLERANCE
    );
    if (offscreen.length === 0) return undefined;

    return (
      offscreen.find((card) => card.getBoundingClientRect().left >= viewport.left - viewport.width) ??
      offscreen[offscreen.length - 1]
    );
  }

  #update = () => {
    if (!this.isConnected) return;

    const { track, previous, next } = this.refs;

    // clientWidth, not offsetWidth: it excludes any scrollbar gutter.
    const maxScroll = track.scrollWidth - track.clientWidth;
    const atStart = track.scrollLeft <= EDGE_TOLERANCE;
    const atEnd = track.scrollLeft >= maxScroll - EDGE_TOLERANCE;

    this.toggleAttribute('overflowing', maxScroll > EDGE_TOLERANCE);
    this.toggleAttribute('at-start', atStart);
    this.toggleAttribute('at-end', atEnd);

    previous?.setAttribute('aria-disabled', String(atStart));
    next?.setAttribute('aria-disabled', String(atEnd));
  };
}

if (!customElements.get('collection-slider-component')) {
  customElements.define('collection-slider-component', CollectionSlider);
}
